import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import InfoPage, { InfoSection } from "@/components/InfoPage";
import { useAuth } from "@/context/AuthContext";
import { LOYALTY } from "@/data/site";
import { formatINR } from "@/data/products";

const earnPercent = Math.round(LOYALTY.earnRate * 100);
const redeemPercent = Math.round(LOYALTY.maxRedeemShare * 100);

const Loyalty = () => {
  const { user, refresh } = useAuth();

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <InfoPage
      eyebrow="My Loyalty Points"
      title="Every bar pays you back."
      intro={`Earn ${earnPercent}% of every order back as DNA points, and spend them on your next box.`}
      testId="loyalty-page"
    >
      <div className="border border-[#EBE8E3] bg-white p-10 md:p-14" data-testid="loyalty-balance-card">
        {user ? (
          <>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-2">Your balance</p>
            <p className="font-serif-lux font-medium text-7xl leading-none" data-testid="loyalty-balance">
              {user.loyaltyPoints || 0}
              <span className="italic font-light text-3xl text-[#5C5C5C]"> points</span>
            </p>
            <p className="text-sm font-light text-[#5C5C5C] mt-4">
              Worth {formatINR((user.loyaltyPoints || 0) * LOYALTY.pointValue)} off your next order.
            </p>
          </>
        ) : (
          <>
            <p className="font-serif-lux text-3xl mb-6">Log in to see your points.</p>
            <Link
              to="/login"
              className="group inline-flex items-center gap-3 bg-[#111111] text-[#F9F8F6] rounded-full px-8 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#C07C5F] transition-colors duration-500"
            >
              Log In
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </Link>
          </>
        )}
      </div>

      <InfoSection heading="How you earn">
        <p>
          Every paid order earns {earnPercent}% of the amount you paid as points — spend {formatINR(1000)}, earn{" "}
          {Math.floor(1000 * LOYALTY.earnRate)} points. Points land in your account as soon as payment is confirmed.
        </p>
      </InfoSection>

      <InfoSection heading="How you redeem">
        <p>
          1 point = {formatINR(LOYALTY.pointValue)}. At checkout, tick "Use my loyalty points" and they come straight off
          your total. Points can cover up to {redeemPercent}% of an order.
        </p>
      </InfoSection>

      <InfoSection heading="The fine print">
        <p>Points are earned on the amount paid, after any points discount, and cannot be exchanged for cash.</p>
        <p>If an order is cancelled, points earned on it are withdrawn and points spent on it are returned.</p>
        <p>Points are linked to your DNA account and cannot be transferred.</p>
      </InfoSection>
    </InfoPage>
  );
};

export default Loyalty;
